import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row
} from 'reactstrap';
import { toast } from 'react-toastify';
import PageHeader from '../common/PageHeader';
import TablePageSizeSelect from '../common/TablePageSizeSelect';
import MedDataTable from '../common/MedDataTable';
import CollapsibleFilterSection from '../common/CollapsibleFilterSection';
import QuillEditor from '../common/QuillEditor';
import ActionConfirmModal from '../common/ActionConfirmModal';
import apiBase from '../../apiConfig';
import useStoredTablePageSize from '../../hooks/useStoredTablePageSize';

const laboratorySections = [
  { key: 'methods', label: "Bo'limlar", accent: 'secondary', statLabel: "bo'lim" },
  { key: 'analyses', label: 'Analizlar', accent: 'info', statLabel: 'analiz' },
  { key: 'legacyMethods', label: 'Metodlar', accent: 'success', statLabel: 'metod' },
  { key: 'templates', label: 'Shablonlar', accent: 'primary', statLabel: 'shablon' },
  { key: 'control', label: 'Boshqarish', accent: 'warning', statLabel: 'jarayon' }
];

const initialTemplateForm = {
  id: null,
  name: '',
  text: '',
  is_active: 1,
  analysis_ids: []
};

const initialMethodForm = {
  id: null,
  title: '',
  category_id: '',
  sort: 0,
  result_type: 0,
  is_active: 1
};

const initialAnalysisForm = {
  id: null,
  title: '',
  short: '',
  price: '',
  method_id: '',
  sort: 0,
  report_sort: 0,
  discount_value: '',
  discount_type: 1,
  days: [],
  protsent: '',
  default_method_id: '',
  queue_disabled: 0,
  availability_status: 1,
  norm_text: '',
  norms: [],
  is_active: 1
};

const initialLegacyMethodForm = {
  id: null,
  title: '',
  lablist_id: '',
  is_active: 1,
  attachments: []
};

const initialControlForm = {
  id: null,
  patient_name: '',
  analysis_title: '',
  method_title: '',
  result: '',
  status: 'pending',
  result_type_value: 0
};

const previewText = value => {
  const plain = String(value || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (plain.length <= 120) {
    return plain || '-';
  }

  return `${plain.slice(0, 120).trim()}...`;
};

const statusColor = status => {
  if (status === 'Chop etilgan') return 'soft-success';
  if (status === 'Tayyor') return 'soft-primary';
  if (status === 'Jarayonda') return 'soft-warning';
  return 'soft-secondary';
};

const attachmentPreview = attachments => {
  if (!Array.isArray(attachments) || attachments.length === 0) {
    return 'Biriktirilmagan';
  }

  return attachments
    .slice(0, 2)
    .map(item => `${item.product_title} (${item.quantity} ${item.type_title || 'dona'})`)
    .join(', ');
};

const Laboratory = () => {
  const [activeSectionKey, setActiveSectionKey] = useState('methods');
  const [pageSize, setPageSize] = useStoredTablePageSize();
  const [search, setSearch] = useState('');
  const [templates, setTemplates] = useState([]);
  const [control, setControl] = useState([]);
  const [legacyMethods, setLegacyMethods] = useState([]);
  const [methods, setMethods] = useState([]);
  const [analyses, setAnalyses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [methodOptions, setMethodOptions] = useState([]);
  const [analysisOptions, setAnalysisOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [counts, setCounts] = useState({
    templates: 0,
    control: 0,
    legacyMethods: 0,
    methods: 0,
    analyses: 0
  });
  const [templateForm, setTemplateForm] = useState(initialTemplateForm);
  const [legacyMethodForm, setLegacyMethodForm] = useState(initialLegacyMethodForm);
  const [methodForm, setMethodForm] = useState(initialMethodForm);
  const [analysisForm, setAnalysisForm] = useState(initialAnalysisForm);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [legacyMethodModalOpen, setLegacyMethodModalOpen] = useState(false);
  const [methodModalOpen, setMethodModalOpen] = useState(false);
  const [analysisModalOpen, setAnalysisModalOpen] = useState(false);
  const [controlModalOpen, setControlModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [controlForm, setControlForm] = useState(initialControlForm);
  const [statusLoadingKey, setStatusLoadingKey] = useState('');

  const activeSection = laboratorySections.find(section => section.key === activeSectionKey) || laboratorySections[0];

  const fetchData = async () => {
    const res = await axios.get(`${apiBase}/laboratory.php`, {
      params: { search, limit: 400 }
    });

    if (res.data.success) {
      setTemplates(res.data.templates || []);
      setControl(res.data.control || []);
      setLegacyMethods(res.data.legacy_methods || []);
      setMethods(res.data.methods || []);
      setAnalyses(res.data.analyses || []);
      setCategories(res.data.categories || []);
      setMethodOptions(res.data.method_options || []);
      setAnalysisOptions(res.data.analysis_options || []);
      setProductOptions(res.data.product_options || []);
      setCounts({
        templates: Number(res.data.counts?.templates || 0),
        control: Number(res.data.counts?.control || 0),
        legacyMethods: Number(res.data.counts?.legacy_methods || 0),
        methods: Number(res.data.counts?.subcategories || res.data.counts?.methods || 0),
        analyses: Number(res.data.counts?.analyses || 0)
      });
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchData().catch(error => {
        toast.error(error.response?.data?.message || "Laboratoriya ma'lumotlari yuklanmadi");
      });
    }, 180);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const closeTemplateModal = () => {
    setTemplateModalOpen(false);
    setTemplateForm(initialTemplateForm);
  };

  const closeLegacyMethodModal = () => {
    setLegacyMethodModalOpen(false);
    setLegacyMethodForm(initialLegacyMethodForm);
  };

  const closeMethodModal = () => {
    setMethodModalOpen(false);
    setMethodForm(initialMethodForm);
  };

  const closeAnalysisModal = () => {
    setAnalysisModalOpen(false);
    setAnalysisForm(initialAnalysisForm);
  };

  const closeControlModal = () => {
    setControlModalOpen(false);
    setControlForm(initialControlForm);
  };

  const openTemplateEdit = item => {
    setTemplateForm({
      id: item.id,
      name: item.name || '',
      text: item.text || '',
      is_active: item.is_active ? 1 : 0,
      analysis_ids: (item.analyses || []).map(analysis => Number(analysis.id))
    });
    setTemplateModalOpen(true);
  };

  const openTemplateCreate = () => {
    setTemplateForm(initialTemplateForm);
    setTemplateModalOpen(true);
  };

  const openLegacyMethodEdit = item => {
    setLegacyMethodForm({
      id: item.id,
      title: item.title || '',
      lablist_id: item.lablist_id || '',
      is_active: item.is_active ? 1 : 0,
      attachments: (item.attachments || []).map(attachment => ({
        product_id: Number(attachment.product_id),
        quantity: attachment.quantity || ''
      }))
    });
    setLegacyMethodModalOpen(true);
  };

  const openLegacyMethodCreate = () => {
    setLegacyMethodForm(initialLegacyMethodForm);
    setLegacyMethodModalOpen(true);
  };

  const openMethodEdit = item => {
    setMethodForm({
      id: item.id,
      title: item.title || '',
      category_id: item.category_id || '',
      sort: Number(item.sort || 0),
      result_type: Number(item.result_type_value || 0),
      is_active: item.is_active ? 1 : 0
    });
    setMethodModalOpen(true);
  };

  const openMethodCreate = () => {
    setMethodForm(initialMethodForm);
    setMethodModalOpen(true);
  };

  const openAnalysisEdit = item => {
    setAnalysisForm({
      id: item.id,
      title: item.title || '',
      short: item.short || '',
      price: item.price || '',
      method_id: item.method_id || '',
      sort: Number(item.sort || 0),
      report_sort: Number(item.report_sort || 0),
      discount_value: item.discount_value || '',
      discount_type: Number(item.discount_type || 1),
      days: item.days || [],
      protsent: item.protsent || '',
      default_method_id: item.default_method_id || '',
      queue_disabled: item.queue_disabled ? 1 : 0,
      availability_status: Number(item.availability_status || 1),
      norm_text: item.norm_text || '',
      norms: (item.norms || []).map(norm => ({
        id: norm.id,
        method_id: norm.method_id || '',
        norm: norm.norm || ''
      })),
      is_active: item.is_active ? 1 : 0
    });
    setAnalysisModalOpen(true);
  };

  const openAnalysisCreate = () => {
    setAnalysisForm(initialAnalysisForm);
    setAnalysisModalOpen(true);
  };

  const openControlEdit = item => {
    setControlForm({
      id: item.id,
      patient_name: item.patient_name || '',
      analysis_title: item.analysis_title || '',
      method_title: item.method_title || '',
      result: item.result || '',
      status: item.status_key || 'pending',
      result_type_value: Number(item.result_type_value || 0)
    });
    setControlModalOpen(true);
  };

  const toggleTemplateAnalysis = analysisId => {
    setTemplateForm(form => ({
      ...form,
      analysis_ids: form.analysis_ids.includes(analysisId)
        ? form.analysis_ids.filter(item => item !== analysisId)
        : [...form.analysis_ids, analysisId]
    }));
  };

  const getLegacyAttachmentQuantity = productId => {
    const found = legacyMethodForm.attachments.find(item => Number(item.product_id) === Number(productId));
    return found ? found.quantity : '';
  };

  const updateLegacyAttachment = (productId, quantity) => {
    setLegacyMethodForm(form => {
      const attachments = [...form.attachments];
      const index = attachments.findIndex(item => Number(item.product_id) === Number(productId));
      const nextQuantity = String(quantity);

      if (nextQuantity.trim() === '') {
        if (index >= 0) {
          attachments.splice(index, 1);
        }
      } else if (index >= 0) {
        attachments[index] = { ...attachments[index], quantity: nextQuantity };
      } else {
        attachments.push({ product_id: Number(productId), quantity: nextQuantity });
      }

      return {
        ...form,
        attachments
      };
    });
  };

  const addAnalysisNorm = () => {
    setAnalysisForm(form => ({
      ...form,
      norms: [...form.norms, { id: null, method_id: '', norm: '' }]
    }));
  };

  const updateAnalysisNorm = (index, field, value) => {
    setAnalysisForm(form => {
      const norms = [...form.norms];
      norms[index] = {
        ...norms[index],
        [field]: value
      };

      return {
        ...form,
        norms
      };
    });
  };

  const removeAnalysisNorm = index => {
    setAnalysisForm(form => ({
      ...form,
      norms: form.norms.filter((_, currentIndex) => currentIndex !== index)
    }));
  };

  const toggleAnalysisDay = day => {
    setAnalysisForm(form => ({
      ...form,
      days: form.days.includes(day) ? form.days.filter(item => item !== day) : [...form.days, day]
    }));
  };

  const handleTemplateSubmit = async e => {
    e.preventDefault();
    if (isSaving) return;

    setIsSaving(true);
    try {
      const payload = {
        entity: 'template',
        name: templateForm.name.trim(),
        text: templateForm.text,
        is_active: Number(templateForm.is_active),
        analysis_ids: templateForm.analysis_ids
      };
      const res = templateForm.id
        ? await axios.put(`${apiBase}/laboratory.php`, { ...payload, id: templateForm.id })
        : await axios.post(`${apiBase}/laboratory.php`, payload);

      toast.success(res.data?.message || (templateForm.id ? 'Shablon yangilandi' : "Shablon qo'shildi"));
      closeTemplateModal();
      fetchData().catch(console.error);
    } catch (error) {
      toast.error(error.response?.data?.message || "Shablonni saqlashda xatolik yuz berdi");
    } finally {
      setIsSaving(false);
    }
  };

  const handleLegacyMethodSubmit = async e => {
    e.preventDefault();
    if (isSaving) return;

    setIsSaving(true);
    try {
      const payload = {
        entity: 'legacy_method',
        title: legacyMethodForm.title.trim(),
        lablist_id: Number(legacyMethodForm.lablist_id),
        is_active: Number(legacyMethodForm.is_active),
        attachments: legacyMethodForm.attachments
          .filter(item => String(item.quantity).trim() !== '')
          .map(item => ({
            product_id: Number(item.product_id),
            quantity: item.quantity
          }))
      };
      const res = legacyMethodForm.id
        ? await axios.put(`${apiBase}/laboratory.php`, { ...payload, id: legacyMethodForm.id })
        : await axios.post(`${apiBase}/laboratory.php`, payload);

      toast.success(res.data?.message || (legacyMethodForm.id ? 'Metod yangilandi' : "Metod qo'shildi"));
      closeLegacyMethodModal();
      fetchData().catch(console.error);
    } catch (error) {
      toast.error(error.response?.data?.message || "Metodni saqlashda xatolik yuz berdi");
    } finally {
      setIsSaving(false);
    }
  };

  const handleMethodSubmit = async e => {
    e.preventDefault();
    if (isSaving) return;

    setIsSaving(true);
    try {
      const payload = {
        entity: 'subcategory',
        title: methodForm.title.trim(),
        category_id: Number(methodForm.category_id),
        sort: Number(methodForm.sort || 0),
        result_type: Number(methodForm.result_type),
        is_active: Number(methodForm.is_active)
      };
      const res = methodForm.id
        ? await axios.put(`${apiBase}/laboratory.php`, { ...payload, entity: 'method', id: methodForm.id })
        : await axios.post(`${apiBase}/laboratory.php`, payload);

      toast.success(res.data?.message || (methodForm.id ? "Bo'lim yangilandi" : "Bo'lim qo'shildi"));
      closeMethodModal();
      fetchData().catch(console.error);
    } catch (error) {
      toast.error(error.response?.data?.message || "Bo'limni saqlashda xatolik yuz berdi");
    } finally {
      setIsSaving(false);
    }
  };

  const handleAnalysisSubmit = async e => {
    e.preventDefault();
    if (isSaving) return;

    setIsSaving(true);
    try {
      const payload = {
        entity: 'analysis',
        title: analysisForm.title.trim(),
        short: analysisForm.short.trim(),
        price: Number(analysisForm.price || 0),
        method_id: Number(analysisForm.method_id),
        sort: Number(analysisForm.sort || 0),
        report_sort: Number(analysisForm.report_sort || 0),
        discount_value: analysisForm.discount_value,
        discount_type: Number(analysisForm.discount_type || 1),
        days: analysisForm.days,
        protsent: analysisForm.protsent,
        default_method_id: Number(analysisForm.default_method_id || 0),
        queue_disabled: Number(analysisForm.queue_disabled || 0),
        availability_status: Number(analysisForm.availability_status || 1),
        norm_text: analysisForm.norm_text,
        norms: analysisForm.norms,
        is_active: Number(analysisForm.is_active)
      };
      const res = analysisForm.id
        ? await axios.put(`${apiBase}/laboratory.php`, { ...payload, id: analysisForm.id })
        : await axios.post(`${apiBase}/laboratory.php`, payload);

      if (analysisForm.id) {
        toast.success(res.data?.message || 'Analiz yangilandi');
      } else {
        toast.success(res.data?.message || "Analiz qo'shildi");
      }
      closeAnalysisModal();
      fetchData().catch(console.error);
    } catch (error) {
      toast.error(error.response?.data?.message || "Analizni saqlashda xatolik yuz berdi");
    } finally {
      setIsSaving(false);
    }
  };

  const handleControlSubmit = async e => {
    e.preventDefault();
    if (isSaving) return;

    setIsSaving(true);
    try {
      const res = await axios.put(`${apiBase}/laboratory.php`, {
        entity: 'control',
        id: controlForm.id,
        result: controlForm.result,
        status: controlForm.status
      });

      toast.success(res.data?.message || 'Laboratoriya natijasi saqlandi');
      closeControlModal();
      fetchData().catch(console.error);
    } catch (error) {
      toast.error(error.response?.data?.message || "Laboratoriya natijasini saqlashda xatolik yuz berdi");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || isSaving) return;

    setIsSaving(true);
    try {
      const res = await axios.delete(`${apiBase}/laboratory.php`, {
        data: {
          entity: deleteTarget.entity,
          id: deleteTarget.id
        }
      });

      toast.success(res.data?.message || "Element o'chirildi");
      setDeleteTarget(null);
      if (deleteTarget.entity === 'template') closeTemplateModal();
      if (deleteTarget.entity === 'legacy_method') closeLegacyMethodModal();
      if (deleteTarget.entity === 'method') closeMethodModal();
      if (deleteTarget.entity === 'analysis') closeAnalysisModal();
      fetchData().catch(console.error);
    } catch (error) {
      toast.error(error.response?.data?.message || "Elementni o'chirishda xatolik yuz berdi");
    } finally {
      setIsSaving(false);
    }
  };

  const handleLegacyMethodStatusToggle = async item => {
    if (statusLoadingKey) return;

    setStatusLoadingKey(`legacy-${item.id}`);
    try {
      await axios.put(`${apiBase}/laboratory.php`, {
        entity: 'legacy_method',
        id: item.id,
        title: item.title,
        lablist_id: item.lablist_id,
        is_active: item.is_active ? 0 : 1,
        attachments: (item.attachments || []).map(attachment => ({
          product_id: Number(attachment.product_id),
          quantity: attachment.quantity
        }))
      });

      fetchData().catch(console.error);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Metod holatini yangilashda xatolik yuz berdi');
    } finally {
      setStatusLoadingKey('');
    }
  };

  const handleSubcategoryStatusToggle = async item => {
    if (statusLoadingKey) return;

    setStatusLoadingKey(`subcategory-${item.id}`);
    try {
      await axios.put(`${apiBase}/laboratory.php`, {
        entity: 'method',
        id: item.id,
        title: item.title,
        category_id: item.category_id,
        sort: item.sort,
        result_type: item.result_type_value,
        is_active: item.is_active ? 0 : 1
      });

      fetchData().catch(console.error);
    } catch (error) {
      toast.error(error.response?.data?.message || "Bo'lim holatini yangilashda xatolik yuz berdi");
    } finally {
      setStatusLoadingKey('');
    }
  };

  const handleAnalysisStatusToggle = async item => {
    if (statusLoadingKey) return;

    setStatusLoadingKey(`analysis-${item.id}`);
    try {
      await axios.put(`${apiBase}/laboratory.php`, {
        entity: 'analysis',
        id: item.id,
        title: item.title,
        short: item.short,
        price: Number(item.price || 0),
        method_id: item.method_id,
        sort: item.sort,
        report_sort: item.report_sort,
        discount_value: item.discount_value,
        discount_type: item.discount_type,
        days: item.days || [],
        protsent: item.protsent,
        default_method_id: item.default_method_id,
        queue_disabled: item.queue_disabled ? 1 : 0,
        availability_status: item.availability_status,
        norm_text: item.norm_text,
        norms: item.norms || [],
        is_active: item.is_active ? 0 : 1
      });

      fetchData().catch(console.error);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Analiz holatini yangilashda xatolik yuz berdi');
    } finally {
      setStatusLoadingKey('');
    }
  };

  const searchPlaceholder =
    activeSectionKey === 'templates'
      ? "Shablon yoki analiz nomi bo'yicha qidirish"
      : activeSectionKey === 'control'
        ? "Bemor, ID yoki analiz bo'yicha qidirish"
        : activeSectionKey === 'legacyMethods'
          ? "Metod yoki analiz bo'yicha qidirish"
          : activeSectionKey === 'methods'
          ? "Bo'lim yoki kategoriya bo'yicha qidirish"
            : "Analiz, qisqa nom yoki kategoriya bo'yicha qidirish";

  const templateColumns = [
    { dataField: 'name', text: 'Nomi', sort: true, classes: 'align-middle font-weight-bold' },
    { dataField: 'text', text: 'Matn', sort: true, classes: 'align-middle text-600', formatter: value => previewText(value) },
    {
      dataField: 'analyses_text',
      text: 'Analizlar',
      sort: true,
      classes: 'align-middle',
      formatter: (value, item) => (
        <div className="med-uzd-table-section-list">
          {(item.analyses || []).map(analysis => (
            <Badge key={`${item.id}-${analysis.id}`} color="soft-info" pill>
              {analysis.short ? `${analysis.title} (${analysis.short})` : analysis.title}
            </Badge>
          ))}
        </div>
      )
    },
    {
      dataField: 'is_active',
      text: 'Holat',
      sort: true,
      classes: 'align-middle',
      formatter: value => <Badge color={value ? 'soft-success' : 'soft-secondary'}>{value ? 'Faol' : 'Nofaol'}</Badge>
    },
    {
      dataField: 'actions',
      text: 'Amallar',
      isDummyField: true,
      headerStyle: { width: '6.5rem' },
      classes: 'align-middle',
      formatter: (data, item) => (
        <div className="med-actions-row">
          <Button type="button" color="link" className="p-0 med-action-btn" onClick={() => openTemplateEdit(item)}>
            <FontAwesomeIcon icon="pencil-alt" />
          </Button>
          <Button
            type="button"
            color="link"
            className="p-0 med-action-btn med-action-btn-danger"
            onClick={() => setDeleteTarget({ entity: 'template', id: item.id, name: item.name })}
          >
            <FontAwesomeIcon icon="trash-alt" />
          </Button>
        </div>
      )
    }
  ];

  const controlColumns = [
    { dataField: 'patient_id', text: 'ID', sort: true, classes: 'align-middle' },
    {
      dataField: 'patient_name',
      text: 'Bemor',
      sort: true,
      classes: 'align-middle',
      formatter: (value, item) => (
        <>
          <div className="font-weight-bold">{value || '-'}</div>
          <small className="text-500">{item.phone || '-'}</small>
        </>
      )
    },
    {
      dataField: 'analysis_title',
      text: 'Analiz',
      sort: true,
      classes: 'align-middle',
      formatter: (value, item) => (
        <>
          <div className="font-weight-semi-bold">{value || '-'}</div>
          <small className="text-500">{item.analysis_short || '-'}</small>
        </>
      )
    },
    { dataField: 'method_title', text: 'Metod', sort: true, classes: 'align-middle', formatter: value => value || '-' },
    { dataField: 'result', text: 'Natija', sort: true, classes: 'align-middle text-600', formatter: value => (value ? previewText(value) : '-') },
    { dataField: 'status', text: 'Holat', sort: true, classes: 'align-middle', formatter: value => <Badge color={statusColor(value)}>{value}</Badge> },
    { dataField: 'date', text: 'Sana', sort: true, classes: 'align-middle', formatter: value => value || '-' },
    {
      dataField: 'actions',
      text: 'Amallar',
      isDummyField: true,
      headerStyle: { width: '4rem' },
      classes: 'align-middle',
      formatter: (data, item) => (
        <div className="med-actions-row">
          <Button type="button" color="link" className="p-0 med-action-btn" onClick={() => openControlEdit(item)}>
            <FontAwesomeIcon icon="pencil-alt" />
          </Button>
        </div>
      )
    }
  ];

  const legacyMethodColumns = [
    { dataField: 'title', text: 'Nomi', sort: true, classes: 'align-middle font-weight-bold' },
    { dataField: 'analysis_title', text: 'Analiz', sort: true, classes: 'align-middle', formatter: value => value || '-' },
    {
      dataField: 'attachments_count',
      text: 'Biriktirilgan reaktivlar',
      sort: true,
      classes: 'align-middle',
      formatter: (value, item) => (
        <>
          <div className="text-600">{attachmentPreview(item.attachments)}</div>
          <small className="text-500">{value || 0} ta reaktiv</small>
        </>
      )
    },
    {
      dataField: 'is_active',
      text: 'Holat',
      sort: true,
      classes: 'align-middle',
      formatter: value => <Badge color={value ? 'soft-success' : 'soft-secondary'}>{value ? 'Faol' : 'Nofaol'}</Badge>
    },
    {
      dataField: 'actions',
      text: 'Amallar',
      isDummyField: true,
      headerStyle: { width: '6.5rem' },
      classes: 'align-middle',
      formatter: (data, item) => (
        <div className="med-actions-row">
          <Button type="button" color="link" className="p-0 med-action-btn" onClick={() => openLegacyMethodEdit(item)}>
            <FontAwesomeIcon icon="pencil-alt" />
          </Button>
          <Button
            type="button"
            color="link"
            className={`p-0 med-action-btn med-action-btn-toggle${item.is_active ? '' : ' is-inactive'}`}
            onClick={() => handleLegacyMethodStatusToggle(item)}
            disabled={statusLoadingKey === `legacy-${item.id}`}
          >
            <FontAwesomeIcon
              icon={statusLoadingKey === `legacy-${item.id}` ? 'spinner' : item.is_active ? 'ban' : 'check-circle'}
              spin={statusLoadingKey === `legacy-${item.id}`}
            />
          </Button>
        </div>
      )
    }
  ];

  const methodColumns = [
    { dataField: 'title', text: 'Nomi', sort: true, classes: 'align-middle font-weight-bold' },
    { dataField: 'category_name', text: 'Kategoriya', sort: true, classes: 'align-middle', formatter: value => value || '-' },
    { dataField: 'sort', text: 'Sort', sort: true, classes: 'align-middle' },
    { dataField: 'result_type', text: "Ko'rinish", sort: true, classes: 'align-middle' },
    {
      dataField: 'is_active',
      text: 'Holat',
      sort: true,
      classes: 'align-middle',
      formatter: value => <Badge color={value ? 'soft-success' : 'soft-secondary'}>{value ? 'Faol' : 'Nofaol'}</Badge>
    },
    {
      dataField: 'actions',
      text: 'Amallar',
      isDummyField: true,
      headerStyle: { width: '7rem' },
      classes: 'align-middle',
      formatter: (data, item) => (
        <div className="med-actions-row">
          <Button type="button" color="link" className="p-0 med-action-btn" onClick={() => openMethodEdit(item)}>
            <FontAwesomeIcon icon="pencil-alt" />
          </Button>
          <Button
            type="button"
            color="link"
            className={`p-0 med-action-btn med-action-btn-toggle${item.is_active ? '' : ' is-inactive'}`}
            onClick={() => handleSubcategoryStatusToggle(item)}
            disabled={statusLoadingKey === `subcategory-${item.id}`}
          >
            <FontAwesomeIcon
              icon={statusLoadingKey === `subcategory-${item.id}` ? 'spinner' : item.is_active ? 'ban' : 'check-circle'}
              spin={statusLoadingKey === `subcategory-${item.id}`}
            />
          </Button>
          <Button
            type="button"
            color="link"
            className="p-0 med-action-btn med-action-btn-danger"
            onClick={() => setDeleteTarget({ entity: 'method', id: item.id, name: item.title })}
          >
            <FontAwesomeIcon icon="trash-alt" />
          </Button>
        </div>
      )
    }
  ];

  const analysisColumns = [
    { dataField: 'title', text: 'Nomi', sort: true, classes: 'align-middle font-weight-bold' },
    { dataField: 'short', text: 'Qisqa nom', sort: true, classes: 'align-middle', formatter: value => value || '-' },
    { dataField: 'category_name', text: 'Kategoriya', sort: true, classes: 'align-middle', formatter: value => value || '-' },
    { dataField: 'subcategory_name', text: "Bo'lim", sort: true, classes: 'align-middle', formatter: value => value || '-' },
    {
      dataField: 'price',
      text: 'Narx',
      sort: true,
      classes: 'align-middle',
      formatter: value => Number(value || 0).toLocaleString('ru-RU')
    },
    {
      dataField: 'is_active',
      text: 'Holat',
      sort: true,
      classes: 'align-middle',
      formatter: value => <Badge color={value ? 'soft-success' : 'soft-secondary'}>{value ? 'Faol' : 'Nofaol'}</Badge>
    },
    {
      dataField: 'actions',
      text: 'Amallar',
      isDummyField: true,
      headerStyle: { width: '7rem' },
      classes: 'align-middle',
      formatter: (data, item) => (
        <div className="med-actions-row">
          <Button type="button" color="link" className="p-0 med-action-btn" onClick={() => openAnalysisEdit(item)}>
            <FontAwesomeIcon icon="pencil-alt" />
          </Button>
          <Button
            type="button"
            color="link"
            className={`p-0 med-action-btn med-action-btn-toggle${item.is_active ? '' : ' is-inactive'}`}
            onClick={() => handleAnalysisStatusToggle(item)}
            disabled={statusLoadingKey === `analysis-${item.id}`}
          >
            <FontAwesomeIcon
              icon={statusLoadingKey === `analysis-${item.id}` ? 'spinner' : item.is_active ? 'ban' : 'check-circle'}
              spin={statusLoadingKey === `analysis-${item.id}`}
            />
          </Button>
          <Button
            type="button"
            color="link"
            className="p-0 med-action-btn med-action-btn-danger"
            onClick={() => setDeleteTarget({ entity: 'analysis', id: item.id, name: item.title })}
          >
            <FontAwesomeIcon icon="trash-alt" />
          </Button>
        </div>
      )
    }
  ];

  return (
    <>
      <PageHeader
        title="Laboratoriya"
        description="Eski laboratoriya menyusidagi bo'limlar endi shu sahifaning o'zida filter ko'rinishida ishlaydi."
        className="mb-3"
      />

      <Card className="border-0 shadow-sm med-dashboard-card med-patient-filter-card mb-3">
        <CardBody className="med-dashboard-card-body">
          <CollapsibleFilterSection
            title="Laboratoriya bo'limlari"
            summary={`${activeSection.label} bo'limi tanlangan`}
            actions={
              <Badge color={`soft-${activeSection.accent}`} pill>
                {activeSection.label}: {counts[activeSection.key] || 0}
              </Badge>
            }
            storageKey="laboratory-sections-collapse"
          >
            <div className="med-patient-filter-grid med-laboratory-filter-grid">
              {laboratorySections.map(section => (
                <button
                  key={section.key}
                  type="button"
                  className={`med-patient-filter-item med-patient-filter-item-${section.accent}${
                    activeSectionKey === section.key ? ' is-active' : ''
                  }`}
                  onClick={() => setActiveSectionKey(section.key)}
                >
                  <span className="med-patient-filter-label">{section.label}</span>
                  <span className="med-patient-filter-meta">
                    <strong>{counts[section.key] || 0}</strong>
                    <small>{section.statLabel}</small>
                  </span>
                </button>
              ))}
            </div>
          </CollapsibleFilterSection>
        </CardBody>
      </Card>

      <Card className="border-0 shadow-sm med-dashboard-card">
        <CardBody className="med-dashboard-card-body">
          <CollapsibleFilterSection
            title="Jadval filtrlari"
            summary={search ? `Qidiruv: ${search}` : "Qidiruv ishlatilmayapti"}
            storageKey={`laboratory-table-filters-${activeSectionKey}`}
          >
            <Row className="mb-3 align-items-center">
              <Col md="auto" className="mb-3 mb-md-0">
                <TablePageSizeSelect value={pageSize} onChange={setPageSize} className="med-table-page-size" />
              </Col>
              <Col md={5} className="mb-3 mb-md-0">
                <Input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="bg-200 border-0"
                />
              </Col>
              <Col className="text-md-right">
                {activeSectionKey === 'templates' && (
                  <Button type="button" color="outline-primary" className="med-outline-add-btn" onClick={openTemplateCreate}>
                    <FontAwesomeIcon icon="plus" className="mr-2" />
                    Yangi shablon
                  </Button>
                )}
                {activeSectionKey === 'legacyMethods' && (
                  <Button type="button" color="outline-success" className="med-outline-add-btn" onClick={openLegacyMethodCreate}>
                    <FontAwesomeIcon icon="plus" className="mr-2" />
                    Yangi metod
                  </Button>
                )}
                {activeSectionKey === 'methods' && (
                  <Button type="button" color="outline-secondary" className="med-outline-add-btn" onClick={openMethodCreate}>
                    <FontAwesomeIcon icon="plus" className="mr-2" />
                    Yangi bo'lim
                  </Button>
                )}
                {activeSectionKey === 'analyses' && (
                  <Button type="button" color="outline-info" className="med-outline-add-btn" onClick={openAnalysisCreate}>
                    <FontAwesomeIcon icon="plus" className="mr-2" />
                    Yangi analiz
                  </Button>
                )}
              </Col>
            </Row>
          </CollapsibleFilterSection>

          {activeSectionKey === 'templates' && (
            <MedDataTable
              data={templates.map(item => ({
                ...item,
                analyses_text: (item.analyses || []).map(analysis => analysis.short ? `${analysis.title} (${analysis.short})` : analysis.title).join(', ')
              }))}
              columns={templateColumns}
              pageSize={pageSize}
              paginationResetKey={`templates-${search}`}
              noDataIndication={<div className="med-dashboard-empty">Laboratoriya shablonlari topilmadi.</div>}
            />
          )}

          {activeSectionKey === 'control' && (
            <MedDataTable
              data={control}
              columns={controlColumns}
              pageSize={pageSize}
              paginationResetKey={`control-${search}`}
              noDataIndication={<div className="med-dashboard-empty">Laboratoriya boshqaruv yozuvlari topilmadi.</div>}
            />
          )}

          {activeSectionKey === 'legacyMethods' && (
            <MedDataTable
              data={legacyMethods}
              columns={legacyMethodColumns}
              pageSize={pageSize}
              paginationResetKey={`legacy-${search}`}
              noDataIndication={<div className="med-dashboard-empty">Laboratoriya metodlari topilmadi.</div>}
            />
          )}

          {activeSectionKey === 'methods' && (
            <MedDataTable
              data={methods}
              columns={methodColumns}
              pageSize={pageSize}
              paginationResetKey={`methods-${search}`}
              noDataIndication={<div className="med-dashboard-empty">Laboratoriya bo'limlari topilmadi.</div>}
            />
          )}

          {activeSectionKey === 'analyses' && (
            <MedDataTable
              data={analyses}
              columns={analysisColumns}
              pageSize={pageSize}
              paginationResetKey={`analyses-${search}`}
              noDataIndication={<div className="med-dashboard-empty">Laboratoriya analizlari topilmadi.</div>}
            />
          )}
        </CardBody>
      </Card>

      <Modal isOpen={templateModalOpen} toggle={closeTemplateModal} size="xl" centered className="med-confirm-modal">
        <ModalHeader toggle={closeTemplateModal}>
          {templateForm.id ? 'Laboratoriya shablonini tahrirlash' : 'Yangi laboratoriya shabloni'}
        </ModalHeader>
        <Form onSubmit={handleTemplateSubmit} className="med-uzd-template-form">
          <ModalBody>
            <Row form>
              <Col md={8}>
                <FormGroup>
                  <Label>Shablon nomi</Label>
                  <Input
                    value={templateForm.name}
                    onChange={e => {
                      const { value } = e.target;
                      setTemplateForm(form => ({ ...form, name: value }));
                    }}
                    className="bg-200 border-0"
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>Holat</Label>
                  <Input
                    type="select"
                    value={templateForm.is_active}
                    onChange={e => {
                      const { value } = e.target;
                      setTemplateForm(form => ({ ...form, is_active: Number(value) }));
                    }}
                    className="bg-200 border-0"
                  >
                    <option value={1}>Faol</option>
                    <option value={0}>Nofaol</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <FormGroup className="mb-3">
              <Label>Matn</Label>
              <div className="med-uzd-editor-wrap">
                <QuillEditor
                  value={templateForm.text}
                  onChange={value => setTemplateForm(form => ({ ...form, text: value }))}
                  className="med-uzd-editor"
                />
              </div>
            </FormGroup>

            <div className="d-flex flex-wrap align-items-center justify-content-between mb-2">
              <div>
                <h6 className="mb-1">Analizlar</h6>
                <small className="text-500">Shablonga biriktirilgan laboratoriya analizlari.</small>
              </div>
              <Badge color="soft-primary" pill>
                Tanlandi: {templateForm.analysis_ids.length}
              </Badge>
            </div>

            <div className="med-uzd-template-section-grid">
              {analysisOptions.map(option => {
                const isSelected = templateForm.analysis_ids.includes(option.id);
                return (
                  <button
                    key={option.id}
                    type="button"
                    className={`med-uzd-template-section${isSelected ? ' is-active' : ''}`}
                    onClick={() => toggleTemplateAnalysis(option.id)}
                  >
                    <span className="med-uzd-template-section-title">{option.title}</span>
                    <span className="med-uzd-template-section-meta">{option.short || 'ANALIZ'}</span>
                  </button>
                );
              })}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button type="button" color="light" onClick={closeTemplateModal}>
              Bekor qilish
            </Button>
            <Button color="primary" type="submit" disabled={isSaving}>
              <FontAwesomeIcon icon={isSaving ? 'spinner' : 'check-circle'} spin={isSaving} className="mr-2" />
              Saqlash
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <Modal isOpen={legacyMethodModalOpen} toggle={closeLegacyMethodModal} size="lg" centered className="med-confirm-modal">
        <ModalHeader toggle={closeLegacyMethodModal}>{legacyMethodForm.id ? 'Metodni tahrirlash' : 'Yangi metod'}</ModalHeader>
        <Form onSubmit={handleLegacyMethodSubmit}>
          <ModalBody>
            <Row form>
              <Col md={8}>
                <FormGroup>
                  <Label>Metod nomi</Label>
                  <Input
                    value={legacyMethodForm.title}
                    onChange={e => {
                      const { value } = e.target;
                      setLegacyMethodForm(form => ({ ...form, title: value }));
                    }}
                    className="bg-200 border-0"
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>Holat</Label>
                  <Input
                    type="select"
                    value={legacyMethodForm.is_active}
                    onChange={e => {
                      const { value } = e.target;
                      setLegacyMethodForm(form => ({ ...form, is_active: Number(value) }));
                    }}
                    className="bg-200 border-0"
                  >
                    <option value={1}>Faol</option>
                    <option value={0}>Nofaol</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>
            <FormGroup>
              <Label>Analiz</Label>
              <Input
                type="select"
                value={legacyMethodForm.lablist_id}
                onChange={e => {
                  const { value } = e.target;
                  setLegacyMethodForm(form => ({ ...form, lablist_id: value }));
                }}
                className="bg-200 border-0"
              >
                <option value="">Tanlang</option>
                {analysisOptions.map(item => (
                  <option key={item.id} value={item.id}>
                    {item.title}
                  </option>
                ))}
              </Input>
            </FormGroup>

            <div className="d-flex flex-wrap align-items-center justify-content-between mb-2">
              <div>
                <h6 className="mb-1">Reaktiv biriktirish</h6>
                <small className="text-500">Metod ishlatadigan reaktiv miqdorlarini belgilang.</small>
              </div>
              <Badge color="soft-success" pill>
                Tanlandi: {legacyMethodForm.attachments.length}
              </Badge>
            </div>

            <div className="med-expense-attachment-editor">
              {productOptions.map(option => (
                <div key={option.id} className="med-expense-attachment-row">
                  <div className="med-expense-attachment-copy">
                    <div className="font-weight-semi-bold">{option.title}</div>
                    <small className="text-500">{option.type_title || "O'lchov ko'rsatilmagan"}</small>
                  </div>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={getLegacyAttachmentQuantity(option.id)}
                    onChange={e => updateLegacyAttachment(option.id, e.target.value)}
                    className="bg-200 border-0"
                    placeholder="0"
                  />
                </div>
              ))}
              {productOptions.length === 0 && <div className="med-dashboard-empty">Reaktivlar topilmadi.</div>}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button type="button" color="light" onClick={closeLegacyMethodModal}>
              Bekor qilish
            </Button>
            <Button color="primary" type="submit" disabled={isSaving}>
              <FontAwesomeIcon icon={isSaving ? 'spinner' : 'check-circle'} spin={isSaving} className="mr-2" />
              Saqlash
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <Modal isOpen={methodModalOpen} toggle={closeMethodModal} centered className="med-confirm-modal">
        <ModalHeader toggle={closeMethodModal}>{methodForm.id ? "Bo'limni tahrirlash" : "Yangi bo'lim"}</ModalHeader>
        <Form onSubmit={handleMethodSubmit}>
          <ModalBody>
            <FormGroup>
              <Label>Bo'lim nomi</Label>
              <Input
                value={methodForm.title}
                onChange={e => {
                  const { value } = e.target;
                  setMethodForm(form => ({ ...form, title: value }));
                }}
                className="bg-200 border-0"
              />
            </FormGroup>
            <FormGroup>
              <Label>Kategoriya</Label>
              <Input
                type="select"
                value={methodForm.category_id}
                onChange={e => {
                  const { value } = e.target;
                  setMethodForm(form => ({ ...form, category_id: value }));
                }}
                className="bg-200 border-0"
              >
                <option value="">Tanlang</option>
                {categories.map(item => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </Input>
            </FormGroup>
            <Row form>
              <Col md={6}>
                <FormGroup>
                  <Label>Sort</Label>
                  <Input
                    type="number"
                    min="0"
                    value={methodForm.sort}
                    onChange={e => {
                      const { value } = e.target;
                      setMethodForm(form => ({ ...form, sort: Number(value) }));
                    }}
                    className="bg-200 border-0"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Ko'rinish</Label>
                  <Input
                    type="select"
                    value={methodForm.result_type}
                    onChange={e => {
                      const { value } = e.target;
                      setMethodForm(form => ({ ...form, result_type: Number(value) }));
                    }}
                    className="bg-200 border-0"
                  >
                    <option value={1}>Bitta jadval</option>
                    <option value={0}>Alohida jadval</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>
            <Row form>
              <Col md={6}>
                <FormGroup>
                  <Label>Holat</Label>
                  <Input
                    type="select"
                    value={methodForm.is_active}
                    onChange={e => {
                      const { value } = e.target;
                      setMethodForm(form => ({ ...form, is_active: Number(value) }));
                    }}
                    className="bg-200 border-0"
                  >
                    <option value={1}>Faol</option>
                    <option value={0}>Nofaol</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button type="button" color="light" onClick={closeMethodModal}>
              Bekor qilish
            </Button>
            <Button color="primary" type="submit" disabled={isSaving}>
              <FontAwesomeIcon icon={isSaving ? 'spinner' : 'check-circle'} spin={isSaving} className="mr-2" />
              Saqlash
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <Modal isOpen={analysisModalOpen} toggle={closeAnalysisModal} size="xl" centered className="med-confirm-modal">
        <ModalHeader toggle={closeAnalysisModal}>{analysisForm.id ? 'Analizni tahrirlash' : 'Yangi analiz'}</ModalHeader>
        <Form onSubmit={handleAnalysisSubmit}>
          <ModalBody>
            <FormGroup>
              <Label>Analiz nomi</Label>
              <Input
                value={analysisForm.title}
                onChange={e => {
                  const { value } = e.target;
                  setAnalysisForm(form => ({ ...form, title: value }));
                }}
                className="bg-200 border-0"
              />
            </FormGroup>
            <Row form>
              <Col md={6}>
                <FormGroup>
                  <Label>Qisqa nom</Label>
                  <Input
                    value={analysisForm.short}
                    onChange={e => {
                      const { value } = e.target;
                      setAnalysisForm(form => ({ ...form, short: value }));
                    }}
                    className="bg-200 border-0"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Narx</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={analysisForm.price}
                    onChange={e => {
                      const { value } = e.target;
                      setAnalysisForm(form => ({ ...form, price: value }));
                    }}
                    className="bg-200 border-0"
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row form>
              <Col md={6}>
                <FormGroup>
                  <Label>Bo'lim</Label>
                  <Input
                    type="select"
                    value={analysisForm.method_id}
                    onChange={e => {
                      const { value } = e.target;
                      setAnalysisForm(form => ({ ...form, method_id: value }));
                    }}
                    className="bg-200 border-0"
                  >
                    <option value="">Tanlang</option>
                    {methodOptions.map(item => (
                      <option key={item.id} value={item.id}>
                        {item.title}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Holat</Label>
                  <Input
                    type="select"
                    value={analysisForm.is_active}
                    onChange={e => {
                      const { value } = e.target;
                      setAnalysisForm(form => ({ ...form, is_active: Number(value) }));
                    }}
                    className="bg-200 border-0"
                  >
                    <option value={1}>Faol</option>
                    <option value={0}>Nofaol</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>
            <Row form>
              <Col md={6}>
                <FormGroup>
                  <Label>Sort</Label>
                  <Input
                    type="number"
                    min="0"
                    value={analysisForm.sort}
                    onChange={e => {
                      const { value } = e.target;
                      setAnalysisForm(form => ({ ...form, sort: Number(value) }));
                    }}
                    className="bg-200 border-0"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Hisobot sort</Label>
                  <Input
                    type="number"
                    min="0"
                    value={analysisForm.report_sort}
                    onChange={e => {
                      const { value } = e.target;
                      setAnalysisForm(form => ({ ...form, report_sort: Number(value) }));
                    }}
                    className="bg-200 border-0"
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row form>
              <Col md={6}>
                <FormGroup>
                  <Label>Chegirma</Label>
                  <Input
                    value={analysisForm.discount_value}
                    onChange={e => {
                      const { value } = e.target;
                      setAnalysisForm(form => ({ ...form, discount_value: value }));
                    }}
                    className="bg-200 border-0"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Chegirma turi</Label>
                  <Input
                    type="select"
                    value={analysisForm.discount_type}
                    onChange={e => {
                      const { value } = e.target;
                      setAnalysisForm(form => ({ ...form, discount_type: Number(value) }));
                    }}
                    className="bg-200 border-0"
                  >
                    <option value={1}>Foiz</option>
                    <option value={2}>Summa</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>
            <Row form>
              <Col md={6}>
                <FormGroup>
                  <Label>Protsent</Label>
                  <Input
                    value={analysisForm.protsent}
                    onChange={e => {
                      const { value } = e.target;
                      setAnalysisForm(form => ({ ...form, protsent: value }));
                    }}
                    className="bg-200 border-0"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Standart metod</Label>
                  <Input
                    type="select"
                    value={analysisForm.default_method_id}
                    onChange={e => {
                      const { value } = e.target;
                      setAnalysisForm(form => ({ ...form, default_method_id: value }));
                    }}
                    className="bg-200 border-0"
                  >
                    <option value="">Tanlanmagan</option>
                    {legacyMethods.map(item => (
                      <option key={item.id} value={item.id}>
                        {item.title}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
            </Row>
            <Row form>
              <Col md={6}>
                <FormGroup>
                  <Label>Navbatga qo'shish</Label>
                  <Input
                    type="select"
                    value={analysisForm.queue_disabled}
                    onChange={e => {
                      const { value } = e.target;
                      setAnalysisForm(form => ({ ...form, queue_disabled: Number(value) }));
                    }}
                    className="bg-200 border-0"
                  >
                    <option value={0}>Ha</option>
                    <option value={1}>Yo'q</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Mavjudlik holati</Label>
                  <Input
                    type="select"
                    value={analysisForm.availability_status}
                    onChange={e => {
                      const { value } = e.target;
                      setAnalysisForm(form => ({ ...form, availability_status: Number(value) }));
                    }}
                    className="bg-200 border-0"
                  >
                    <option value={1}>Analiz bor</option>
                    <option value={0}>Analiz vaqtincha yo'q</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <FormGroup>
              <Label>Kunlar bo'yicha chegirma</Label>
              <div className="med-uzd-template-section-grid">
                {[
                  ['1', 'Dush'],
                  ['2', 'Sesh'],
                  ['3', 'Chor'],
                  ['4', 'Pay'],
                  ['5', 'Jum'],
                  ['6', 'Shan'],
                  ['7', 'Yak']
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    className={`med-uzd-template-section${analysisForm.days.includes(value) ? ' is-active' : ''}`}
                    onClick={() => toggleAnalysisDay(value)}
                  >
                    <span className="med-uzd-template-section-title">{label}</span>
                    <span className="med-uzd-template-section-meta">kun</span>
                  </button>
                ))}
              </div>
            </FormGroup>

            <FormGroup>
              <Label>Norma matni</Label>
              <Input
                type="textarea"
                rows="3"
                value={analysisForm.norm_text}
                onChange={e => {
                  const { value } = e.target;
                  setAnalysisForm(form => ({ ...form, norm_text: value }));
                }}
                className="bg-200 border-0"
              />
            </FormGroup>

            <div className="d-flex flex-wrap align-items-center justify-content-between mb-2">
              <div>
                <h6 className="mb-1">Normlar</h6>
                <small className="text-500">Har bir norma uchun metod va qiymat kiriting.</small>
              </div>
              <Button type="button" color="light" onClick={addAnalysisNorm}>
                <FontAwesomeIcon icon="plus" className="mr-2" />
                Norma qo'shish
              </Button>
            </div>

            <div className="med-expense-attachment-editor">
              {analysisForm.norms.map((normItem, index) => (
                <div key={`${normItem.id || 'new'}-${index}`} className="med-expense-attachment-row">
                  <Input
                    type="select"
                    value={normItem.method_id}
                    onChange={e => updateAnalysisNorm(index, 'method_id', e.target.value)}
                    className="bg-200 border-0"
                  >
                    <option value="">Metod tanlang</option>
                    {legacyMethods.map(item => (
                      <option key={item.id} value={item.id}>
                        {item.title}
                      </option>
                    ))}
                  </Input>
                  <div className="d-flex align-items-center">
                    <Input
                      value={normItem.norm}
                      onChange={e => updateAnalysisNorm(index, 'norm', e.target.value)}
                      className="bg-200 border-0 mr-2"
                      placeholder="Norma"
                    />
                    <Button type="button" color="link" className="p-0 med-action-btn med-action-btn-danger" onClick={() => removeAnalysisNorm(index)}>
                      <FontAwesomeIcon icon="trash-alt" />
                    </Button>
                  </div>
                </div>
              ))}
              {analysisForm.norms.length === 0 && <div className="med-dashboard-empty">Normlar qo'shilmagan.</div>}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button type="button" color="light" onClick={closeAnalysisModal}>
              Bekor qilish
            </Button>
            <Button color="primary" type="submit" disabled={isSaving}>
              <FontAwesomeIcon icon={isSaving ? 'spinner' : 'check-circle'} spin={isSaving} className="mr-2" />
              Saqlash
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <Modal isOpen={controlModalOpen} toggle={closeControlModal} centered className="med-confirm-modal">
        <ModalHeader toggle={closeControlModal}>Analiz natijasini kiritish</ModalHeader>
        <Form onSubmit={handleControlSubmit}>
          <ModalBody>
            <Row form>
              <Col md={6}>
                <FormGroup>
                  <Label>Bemor</Label>
                  <Input value={controlForm.patient_name} disabled className="bg-200 border-0" />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Analiz</Label>
                  <Input value={controlForm.analysis_title} disabled className="bg-200 border-0" />
                </FormGroup>
              </Col>
            </Row>

            <FormGroup>
              <Label>Metod</Label>
              <Input value={controlForm.method_title} disabled className="bg-200 border-0" />
            </FormGroup>

            <FormGroup>
              <Label>Holat</Label>
              <Input
                type="select"
                value={controlForm.status}
                onChange={e => {
                  const { value } = e.target;
                  setControlForm(form => ({ ...form, status: value }));
                }}
                className="bg-200 border-0"
              >
                <option value="pending">Kutilmoqda</option>
                <option value="in_progress">Jarayonda</option>
                <option value="ready">Tayyor</option>
                <option value="printed">Chop etilgan</option>
              </Input>
            </FormGroup>

            {controlForm.result_type_value === 1 ? (
              <FormGroup>
                <Label>Qiymat</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={controlForm.result}
                  onChange={e => {
                    const { value } = e.target;
                    setControlForm(form => ({ ...form, result: value }));
                  }}
                  className="bg-200 border-0"
                  placeholder="Raqamli natijani kiriting"
                />
              </FormGroup>
            ) : (
              <FormGroup>
                <Label>Natija</Label>
                <Input
                  type="textarea"
                  rows="5"
                  value={controlForm.result}
                  onChange={e => {
                    const { value } = e.target;
                    setControlForm(form => ({ ...form, result: value }));
                  }}
                  className="bg-200 border-0"
                  placeholder="Analiz natijasini kiriting"
                />
              </FormGroup>
            )}
          </ModalBody>
          <ModalFooter>
            <Button type="button" color="light" onClick={closeControlModal}>
              Bekor qilish
            </Button>
            <Button color="primary" type="submit" disabled={isSaving}>
              <FontAwesomeIcon icon={isSaving ? 'spinner' : 'check-circle'} spin={isSaving} className="mr-2" />
              Saqlash
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <ActionConfirmModal
        isOpen={Boolean(deleteTarget)}
        toggle={() => setDeleteTarget(null)}
        title="Elementni o'chirish"
        message={deleteTarget ? `${deleteTarget.name} elementini o'chirmoqchimisiz?` : ''}
        confirmLabel="O'chirish"
        onConfirm={handleDelete}
      />
    </>
  );
};

export default Laboratory;
